import dayjs from "dayjs";
import * as React from "react";

import type { DateRange, QuickFilterOption } from "@/shared/components";

interface UseQuickFiltersProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  quickFilters: QuickFilterOption[];
  defaultQuickFilter?: QuickFilterOption;
}

export const useQuickFilters = ({
  dateRange,
  onDateRangeChange,
  quickFilters,
  defaultQuickFilter,
}: UseQuickFiltersProps) => {
  const currentTimezone = dayjs.tz.guess();

  // Track the intended filter state to handle async state updates
  const [intendedFilter, setIntendedFilter] =
    React.useState<QuickFilterOption | null>(null);

  // Quick time range setter
  const setQuickTimeRange = React.useCallback(
    (amount: number | null, unit: dayjs.ManipulateType) => {
      // Find the filter that matches this amount/unit
      const matchingFilter = quickFilters.find(
        (f) => f.amount === amount && f.unit === unit,
      );
      setIntendedFilter(matchingFilter || null);

      if (amount === null) {
        // "All Time" - no time constraints
        onDateRangeChange({
          startDate: null,
          endDate: null,
        });
      } else {
        const now = dayjs().tz(currentTimezone);
        const startTime = now.subtract(amount, unit);
        onDateRangeChange({
          startDate: startTime,
          endDate: now,
        });
      }
    },
    [currentTimezone, onDateRangeChange, quickFilters],
  );

  // Function to check if a quick filter matches the current date range
  const isQuickFilterActive = React.useCallback(
    (filter: QuickFilterOption): boolean => {
      // If we have an intended filter (from a recent click), use that for immediate feedback
      if (intendedFilter) {
        return (
          filter.amount === intendedFilter.amount &&
          filter.unit === intendedFilter.unit &&
          filter.label === intendedFilter.label
        );
      }

      // Handle "All Time" filter (amount is null)
      if (filter.amount === null) {
        return !dateRange.startDate && !dateRange.endDate;
      }

      // Handle regular time-based filters
      if (!dateRange.startDate || !dateRange.endDate) {
        return false;
      }

      const expectedStart = dayjs()
        .tz(currentTimezone)
        .subtract(filter.amount, filter.unit);
      const expectedEnd = dayjs().tz(currentTimezone);

      // Allow for small timing differences (within 2 minutes)
      const startDiff = Math.abs(
        dateRange.startDate.diff(expectedStart, "minute"),
      );
      const endDiff = Math.abs(dateRange.endDate.diff(expectedEnd, "minute"));

      return startDiff <= 2 && endDiff <= 2;
    },
    [intendedFilter, dateRange.startDate, dateRange.endDate, currentTimezone],
  );

  // Find the currently active quick filter
  const activeQuickFilter = React.useMemo(() => {
    return quickFilters.find(isQuickFilterActive) || null;
  }, [quickFilters, isQuickFilterActive]);

  // Set default quick filter on mount if provided and no date range is set
  // Use a ref to track if we've already initialized to avoid overriding explicit selections
  const hasInitialized = React.useRef(false);

  React.useEffect(() => {
    if (
      defaultQuickFilter &&
      !dateRange.startDate &&
      !dateRange.endDate &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true;
      setIntendedFilter(defaultQuickFilter);
      setQuickTimeRange(defaultQuickFilter.amount, defaultQuickFilter.unit);
    }
  }, [
    defaultQuickFilter,
    dateRange.startDate,
    dateRange.endDate,
    setQuickTimeRange,
  ]);

  // Mark as initialized when any explicit date range change occurs
  React.useEffect(() => {
    if (dateRange.startDate || dateRange.endDate) {
      hasInitialized.current = true;
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Clear intended filter when the actual date range matches it
  React.useEffect(() => {
    if (intendedFilter) {
      // Check if the current date range matches the intended filter
      if (intendedFilter.amount === null) {
        // "All Time" case
        if (!dateRange.startDate && !dateRange.endDate) {
          setIntendedFilter(null);
        }
      } else {
        // Regular time-based filter case
        if (dateRange.startDate && dateRange.endDate) {
          const expectedStart = dayjs()
            .tz(currentTimezone)
            .subtract(intendedFilter.amount, intendedFilter.unit);
          const expectedEnd = dayjs().tz(currentTimezone);

          const startDiff = Math.abs(
            dateRange.startDate.diff(expectedStart, "minute"),
          );
          const endDiff = Math.abs(
            dateRange.endDate.diff(expectedEnd, "minute"),
          );

          if (startDiff <= 2 && endDiff <= 2) {
            setIntendedFilter(null);
          }
        }
      }
    }
  }, [intendedFilter, dateRange.startDate, dateRange.endDate, currentTimezone]);

  return {
    setQuickTimeRange,
    isQuickFilterActive,
    activeQuickFilter,
  };
};
